import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { formatNumber } from 'src/utils/number/numberUtils';

import { PRD3060_R01DetailDto, defaultPRD3060_R01DetailData } from './Dto_PRD3060_R01';
import { usePRD3060_R01Store } from './Store_PRD3060_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const DETAIL_COLUMN_STATE_KEY = 'PRD3060_R01-detail-grid-column-state';

export const Detailtable_PRD3060_R01 = () => {
	const gridRef = useRef<AgGridReact<PRD3060_R01DetailDto>>(null);

	const {
		PRD3060_R01DetailList,
		detailColumnState,
		setDetailColumnState,
		detailScrollTop,
		setDetailScrollTop,
	} = usePRD3060_R01Store();

	const [, setHoveredRowIndex] = useState<number | null>(null);

	const { getRowId, settingRowIndex, createRowClassRules, paddedGridData } = useGridSettingContent({
		gridRef,
		key: 'badCase',
		gridData: PRD3060_R01DetailList,
		defaultGridData: defaultPRD3060_R01DetailData,
		selectedRow: [],
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		badCaseNm: '불량유형',
		badCaseQty: '불량수량',
	};

	const getCellStyle = useCallback((params: any, key: string) => onlyTouchGridStyle(key, "badCaseNm")(params), []);

	const baseColumnDefs: ColDef<PRD3060_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '불량유형', colId: 'badCaseNm', field: 'badCaseNm', width: 150,
			editable: false,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, "badCaseNm"),
		},
		{
			headerName: '불량수량', colId: 'badCaseQty', field: 'badCaseQty', width: 120,
			editable: false,
			type: 'numericColumn',
			headerClass: aggridHeaderClass,
			valueFormatter: (p) => formatNumber(p.value || 0),
			cellStyle: (p) => getCellStyle(p, "badCaseQty"),
		},
	], []);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs = useMemo(() => {
		if (detailColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<PRD3060_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [detailColumnState, settingRowIndex, baseColumnDefs, headerNames]);

	const pinnedBottomRow = useMemo(
		() => ([{
			badCaseNm: `${PRD3060_R01DetailList.length.toLocaleString()}건`,
			badCaseQty: PRD3060_R01DetailList.reduce((s, r) => s + (Number(r.badCaseQty) || 0), 0),
		}]),
		[PRD3060_R01DetailList],
	);

	return (
		<div className="flex flex-col h-full min-h-0">
			<GridContextWrapper<PRD3060_R01DetailDto>
				enableCellCalculation
				gridRef={gridRef}
				paddedGridData={paddedGridData}
				initSelected={() => {}}
				defaultRowData={defaultPRD3060_R01DetailData}
				baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
				defaultColumnState={defaultColumnState}
				setColumnState={setDetailColumnState}
				localStorageKey={DETAIL_COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Touch<PRD3060_R01DetailDto>
					ref={gridRef}
					rowData={paddedGridData}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					getRowId={getRowId}
					onCellKeyDown={undefined}
					rowClassRules={createRowClassRules<PRD3060_R01DetailDto>({
						idKey: 'badCase',
						selectedList: [],
						deletedList: [],
					})}
					pinnedBottomRowData={pinnedBottomRow}
					setHoveredRowIndex={setHoveredRowIndex}
					columnStateKey={DETAIL_COLUMN_STATE_KEY}
					setColumnState={setDetailColumnState}
					scrollTop={detailScrollTop}
					setScrollTop={setDetailScrollTop}
					getRowStyle={(p) =>
						p.node.rowPinned === 'bottom'
							? { background: '#F0F0F0', fontWeight: 'bold' }
							: undefined
					}
				/>
			</GridContextWrapper>
		</div>
	);
};
